/**
 * Elevator installer appointments API.
 *
 * An appointment is a scheduled site visit to install the automated dry-touch
 * robot call system. Lifecycle: Scheduled -> Completed | Void. The multi-section
 * intake checklist is stored in `checklist_data` keyed by field id
 * (see src/data/elevatorIntakeForm.ts).
 */

import { API_BASE } from '../config/apiBase';
import { AttachmentVoidFields, patchAttachmentVoid } from './attachmentVoid';

export type ElevatorAppointmentStatus = 'Scheduled' | 'Completed' | 'Void';

/** Answers keyed by field id. Values are string | boolean | array-of-row-objects. */
export type ChecklistData = Record<string, unknown>;

export interface ElevatorAssignee {
  user_id: number;
  username: string;
}

export interface ElevatorAppointmentRow {
  id: number;
  client_id: number | null;
  /** The linked client's Job Code / Deployment ID, e.g. A-000101. */
  job_code: string | null;
  order_number: string | null;
  business_name: string;
  business_address: string | null;
  point_of_contact: string | null;
  poc_phone: string | null;
  poc_email: string | null;
  appointment_at: string | null;
  status: ElevatorAppointmentStatus;
  void_reason: string | null;
  checklist_data: ChecklistData;
  task_id: number | null;
  public_token: string | null;
  conflict_reason: string | null;
  conflict_author_name: string | null;
  conflict_reported_at: string | null;
  conflict_resolved_at: string | null;
  assignees: ElevatorAssignee[];
  created_by_user_id: number | null;
  created_by_username: string | null;
  completed_at: string | null;
  voided_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface ElevatorAppointmentsListResponse {
  scheduled: ElevatorAppointmentRow[];
  completed: ElevatorAppointmentRow[];
  void: ElevatorAppointmentRow[];
}

export async function fetchElevatorAppointments(): Promise<ElevatorAppointmentsListResponse> {
  const res = await fetch(`${API_BASE}/api/elevator-appointments`);
  if (!res.ok) throw new Error('Failed to fetch appointments');
  return res.json();
}

export async function fetchElevatorAppointment(id: number): Promise<ElevatorAppointmentRow> {
  const res = await fetch(`${API_BASE}/api/elevator-appointments/${id}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to fetch appointment');
  return data as ElevatorAppointmentRow;
}

export interface CreateElevatorAppointmentPayload {
  user_id: number;
  client_id?: number | null;
  business_name: string;
  business_address?: string | null;
  point_of_contact?: string | null;
  poc_phone?: string | null;
  poc_email?: string | null;
  appointment_at?: string | null;
  assignee_user_ids?: number[];
  /** When set, links the created appointment to an initial survey and marks it Scheduled. */
  survey_id?: number | null;
}

export async function createElevatorAppointment(
  payload: CreateElevatorAppointmentPayload
): Promise<ElevatorAppointmentRow> {
  const res = await fetch(`${API_BASE}/api/elevator-appointments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to create appointment');
  return data as ElevatorAppointmentRow;
}

export interface UpdateElevatorAppointmentPayload {
  business_name?: string;
  business_address?: string | null;
  point_of_contact?: string | null;
  poc_phone?: string | null;
  poc_email?: string | null;
  appointment_at?: string | null;
  checklist_data?: ChecklistData;
  status?: ElevatorAppointmentStatus;
  void_reason?: string | null;
  /** Clears the outstanding scheduling-conflict flag. */
  resolve_conflict?: boolean;
}

export async function updateElevatorAppointment(
  id: number,
  payload: UpdateElevatorAppointmentPayload
): Promise<ElevatorAppointmentRow> {
  const res = await fetch(`${API_BASE}/api/elevator-appointments/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to update appointment');
  return data as ElevatorAppointmentRow;
}

export async function completeElevatorAppointment(id: number): Promise<ElevatorAppointmentRow> {
  return updateElevatorAppointment(id, { status: 'Completed' });
}

export async function voidElevatorAppointment(id: number, reason: string): Promise<ElevatorAppointmentRow> {
  return updateElevatorAppointment(id, { status: 'Void', void_reason: reason });
}

/** Reschedule to a new date/time; this also resolves any outstanding conflict. */
export async function rescheduleElevatorAppointment(id: number, appointmentAtIso: string): Promise<ElevatorAppointmentRow> {
  return updateElevatorAppointment(id, { appointment_at: appointmentAtIso, resolve_conflict: true });
}

/** True when the appointment has an unresolved client-reported scheduling conflict. */
export function hasOpenConflict(r: {
  conflict_reported_at: string | null;
  conflict_resolved_at: string | null;
}): boolean {
  return !!r.conflict_reported_at && !r.conflict_resolved_at;
}

// ── Public schedule-conflict form (no auth; keyed by opaque token) ──

export interface PublicElevatorConflictInfo {
  business_name: string;
  business_address: string | null;
  appointment_at: string | null;
  status: ElevatorAppointmentStatus;
}

export async function fetchPublicElevatorConflict(token: string): Promise<PublicElevatorConflictInfo> {
  const res = await fetch(`${API_BASE}/api/public/elevator-conflict/${encodeURIComponent(token)}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to load appointment');
  return data as PublicElevatorConflictInfo;
}

export async function submitElevatorConflict(token: string, reason: string, authorName?: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/public/elevator-conflict/${encodeURIComponent(token)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason, author_name: authorName }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to submit');
}

// ── Elevator initial surveys ──

export type ElevatorSurveyStatus = 'Sent' | 'Submitted' | 'Scheduled';

export interface ElevatorInitialSurveyRow {
  id: number;
  public_token: string;
  client_id: number | null;
  order_number: string | null;
  contract_id: string | null;
  business_name: string;
  status: ElevatorSurveyStatus;
  form_data: Record<string, unknown>;
  submitted_at: string | null;
  elevator_appointment_id: number | null;
  created_by_user_id: number | null;
  created_at: string | null;
  updated_at: string | null;
  /** Set once the link has been retired; a retired link no longer opens. */
  revoked_at: string | null;
  revoked_by_user_id: number | null;
  /** Public URL the client uses to fill the survey out. */
  link: string;
  /** Whether a contract has been sent for this survey's order/contract. */
  has_contract: boolean;
  /** Whether that contract has been signed (gates appointment scheduling). */
  contract_signed: boolean;
}

export interface ElevatorSurveyPhoto extends AttachmentVoidFields {
  id: number;
  survey_id: number;
  /** Matches an id in ELEVATOR_SURVEY_PHOTOS. */
  field_id: string;
  original_file_name: string;
  mime_type: string | null;
  byte_size: number | null;
  created_at: string | null;
}

export async function fetchElevatorInitialSurveys(): Promise<ElevatorInitialSurveyRow[]> {
  const res = await fetch(`${API_BASE}/api/elevator-initial-surveys`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to load surveys');
  return (data as { surveys: ElevatorInitialSurveyRow[] }).surveys || [];
}

export async function fetchElevatorInitialSurvey(
  id: number
): Promise<ElevatorInitialSurveyRow & { photos: ElevatorSurveyPhoto[] }> {
  const res = await fetch(`${API_BASE}/api/elevator-initial-surveys/${id}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to load survey');
  return data as ElevatorInitialSurveyRow & { photos: ElevatorSurveyPhoto[] };
}

/** Staff-facing URL for a photo the client uploaded with the survey. */
export function elevatorSurveyPhotoUrl(surveyId: number, photoId: number): string {
  return `${API_BASE}/api/elevator-initial-surveys/${surveyId}/photos/${photoId}/file`;
}

// ─── Survey links on an order ──────────────────────────────────────────────

export async function fetchOrderElevatorSurveyLinks(orderNumber: string): Promise<ElevatorInitialSurveyRow[]> {
  const res = await fetch(`${API_BASE}/api/orders/${encodeURIComponent(orderNumber)}/elevator-survey-links`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to load survey links');
  return (data as { surveys: ElevatorInitialSurveyRow[] }).surveys || [];
}

/** Issues a new link and retires the order's previous unanswered one. */
export async function generateOrderElevatorSurveyLink(
  orderNumber: string,
  userId?: number
): Promise<ElevatorInitialSurveyRow> {
  const res = await fetch(`${API_BASE}/api/orders/${encodeURIComponent(orderNumber)}/elevator-survey-links`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to generate survey link');
  return data as ElevatorInitialSurveyRow;
}

export async function revokeElevatorSurveyLink(id: number, userId?: number): Promise<ElevatorInitialSurveyRow> {
  const res = await fetch(`${API_BASE}/api/elevator-initial-surveys/${id}/revoke`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to disable survey link');
  return data as ElevatorInitialSurveyRow;
}

// ─── Public survey form ────────────────────────────────────────────────────

export interface PublicElevatorSurveyInfo {
  business_name: string;
  status: ElevatorSurveyStatus;
  form_data: Record<string, unknown>;
  already_submitted: boolean;
  photos: ElevatorSurveyPhoto[];
}

export async function fetchPublicElevatorSurvey(token: string): Promise<PublicElevatorSurveyInfo> {
  const res = await fetch(`${API_BASE}/api/public/elevator-survey/${encodeURIComponent(token)}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to load survey');
  return data as PublicElevatorSurveyInfo;
}

export async function submitPublicElevatorSurvey(
  token: string,
  formData: Record<string, unknown>,
  pdfBase64?: string
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/public/elevator-survey/${encodeURIComponent(token)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ form_data: formData, pdf_base64: pdfBase64 }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to submit survey');
}

export function publicElevatorSurveyPhotoUrl(token: string, photoId: number): string {
  return `${API_BASE}/api/public/elevator-survey/${encodeURIComponent(token)}/photos/${photoId}/file`;
}

export async function uploadPublicElevatorSurveyPhoto(
  token: string,
  fieldId: string,
  file: File
): Promise<ElevatorSurveyPhoto> {
  const form = new FormData();
  form.append('file', file);
  form.append('field_id', fieldId);
  const res = await fetch(`${API_BASE}/api/public/elevator-survey/${encodeURIComponent(token)}/photos`, {
    method: 'POST',
    body: form,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to upload photo');
  return data as ElevatorSurveyPhoto;
}

/**
 * Sets a photo aside with a reason instead of deleting it, so the survey keeps a
 * record of what was removed. The slot frees up for a new upload either way.
 */
export function voidPublicElevatorSurveyPhoto(
  token: string,
  photoId: number,
  reason: string
): Promise<ElevatorSurveyPhoto> {
  return patchAttachmentVoid(
    `${API_BASE}/api/public/elevator-survey/${encodeURIComponent(token)}/photos/${photoId}`,
    reason
  );
}

export function unvoidPublicElevatorSurveyPhoto(token: string, photoId: number): Promise<ElevatorSurveyPhoto> {
  return patchAttachmentVoid(
    `${API_BASE}/api/public/elevator-survey/${encodeURIComponent(token)}/photos/${photoId}`,
    null
  );
}
